import {
  addTeamMemberRequestSchema,
  businessSchema,
  businessShape,
  extractTimetableResponseSchema,
  importReviewSchema,
  importedContactSchema,
  invitePreviewSchema,
  inviteStatusSchema,
  okSchema,
  ownerParticipantSchema,
  ownerScheduleResponseSchema,
  packagesAndRulesSchema,
  participantsResponseSchema,
  sendResultSchema,
  setupProgressSchema,
  startingBalancesSchema,
  teamMemberSchema,
  teamResponseSchema,
  timetableCardSchema,
  timetableSchema,
  type AddTeamMemberRequest,
  type BookingFor,
  type Business,
  type BusinessSession,
  type ClassFill,
  type ExtractTimetableResponse,
  type HeardFrom,
  type TimetableFile,
  type ImportContactsRequest,
  type ImportReview,
  type ImportedContact,
  type ImportClassification,
  type InvitePreview,
  type InviteStatus,
  type JobTitle,
  type OwnerParticipant,
  type PackagesAndRules,
  type PlaceParticipantRequest,
  type SaveStartingBalancesRequest,
  type SavePackagesRequest,
  type SetupProgress,
  type Sport,
  type StartingBalances,
  type TeamMember,
  type TeamRole,
  type Timetable,
  type TimetableCard,
  type UpdateBusinessRequest,
} from "@teamna/shared";
import { setAnalyticsShape } from "../analytics/web.ts";
import { clearSignupCampaign, readSignupCampaign } from "../lib/signup-campaign.ts";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { z } from "zod";
import { api } from "../api/client.ts";
import { businessScheduleQueries, notBookedQueries, queryKeys } from "./keys.ts";
import { setActiveBusinessId } from "./use-owner.ts";

/**
 * The setup checklist, Home and the first-day fork.
 *
 * Every hook here is a port of its twin in `apps/mobile/src/hooks/use-club.ts`, against the
 * same `/v1/business/businesses/:id/*` routes, so the web office and the phone cannot disagree on
 * what a step needs or what "done" means. A setup step is done because of the data the other pages
 * write (a template exists, a package exists), so every save here refreshes the whole `setup`
 * family for the business, not only its own list.
 */

const base = (businessId: string) => `/business/businesses/${businessId}`;

/** Refreshes every setup query for one business, and the owner-side lists they feed. */
export function refreshSetup(queryClient: QueryClient, businessId: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.setup.all(businessId) });
  // Every setup write is something the member home reads (venue, days off, rules, placement and
  // usual days, approvals, balances), and an owner can be a member of their own business too.
  void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
  // Placement and usual days decide who is on a Did not book list.
  void queryClient.invalidateQueries(notBookedQueries);
}

// --- create business (the first-day fork) ------------------------------------

/** "Blue Wave Swim" to "blue-wave-swim". */
export function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export type CreateBusinessInput = {
  name: string;
  sport: Sport;
  /** The business shape (migration 0069). Left out, the API uses the sport's start value. */
  bookingFor?: BookingFor;
  classFill?: ClassFill;
  venueName?: string | null;
  daysOff?: number[];
  /** "Your role at the business" on the owner sign-up. A label only. */
  jobTitle?: JobTitle;
  /** "How did you hear about Teamna?" on the owner sign-up (task 0.7). */
  heardFrom?: HeardFrom;
  /** The owner who told them. Sent only with `heardFrom: another_owner`. */
  referrerName?: string | null;
};

/**
 * POST /v1/owner/businesses
 *
 * The slug is required by the contract but nobody should have to think about a URL while naming
 * their business, so it is derived here, with a short random tail so two businesses with the same
 * name cannot collide (the same rule as the mobile hook). The new business becomes the active one.
 */
export function useCreateBusiness(): UseMutationResult<Business, unknown, CreateBusinessInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const stem = slugFromName(input.name).slice(0, 48) || "business";
      const tail = Math.random().toString(36).slice(2, 7);
      return api.post(
        "/owner/businesses",
        {
          name: input.name,
          slug: `${stem}-${tail}`,
          sport: input.sport,
          ...(input.bookingFor ? { bookingFor: input.bookingFor } : {}),
          ...(input.classFill ? { classFill: input.classFill } : {}),
          ...(input.venueName !== undefined ? { venueName: input.venueName } : {}),
          ...(input.daysOff ? { daysOff: input.daysOff } : {}),
          ...(input.jobTitle ? { jobTitle: input.jobTitle } : {}),
          ...(input.heardFrom ? { heardFrom: input.heardFrom } : {}),
          ...(input.heardFrom === "another_owner" && input.referrerName?.trim() ? { referrerName: input.referrerName.trim() } : {}),
          // The website campaign kept since the first Desk page (task 0.7).
          ...readSignupCampaign(),
        },
        businessSchema,
      );
    },
    // Wait for the fresh businesses list before the caller moves on, as `useJoinBusiness` does. No
    // screen watches the list on Create business, so the default refetch of active queries does not
    // wait, and the owner office then reads the empty list the landing cached and sends the new
    // owner back to the first-day fork (a second Create made a second business, 2026-10-08).
    onSuccess: async (business) => {
      clearSignupCampaign();
      setActiveBusinessId(business.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.businesses, refetchType: "all" });
    },
  });
}

/** PATCH /v1/owner/businesses/:id, for the business step (name, sport, venue, days off). */
export function useUpdateBusiness(
  businessId: string,
): UseMutationResult<Business, unknown, UpdateBusinessRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.patch(`/owner/businesses/${businessId}`, input, businessSchema),
    onSuccess: (business) => {
      queryClient.setQueryData(queryKeys.owner.business(businessId), business);
      // A new shape goes on the next analytics events at once, not at the next place change.
      setAnalyticsShape(business.id, businessShape(business));
      void queryClient.invalidateQueries({ queryKey: queryKeys.businesses });
      refreshSetup(queryClient, businessId);
    },
    // A save can fail after the server committed it (a later step of the route failed). Read the
    // business again, so a switch shows what the server holds, not the value from before.
    onError: () => void queryClient.invalidateQueries({ queryKey: queryKeys.owner.business(businessId) }),
  });
}

// --- progress ---------------------------------------------------------------

/** GET /business/businesses/:id/setup */
export function useSetupProgress(businessId: string | null): UseQueryResult<SetupProgress> {
  return useQuery({
    queryKey: queryKeys.setup.progress(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/setup`, setupProgressSchema),
    enabled: Boolean(businessId),
    staleTime: 30 * 1000,
  });
}

/** POST /business/businesses/:id/setup/:step, for the two steps the data cannot tell on its own. */
export function useMarkSetupStep(
  businessId: string,
): UseMutationResult<SetupProgress, unknown, "team" | "members_imported"> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (step) => api.post(`${base(businessId)}/setup/${step}`, undefined, setupProgressSchema),
    onSuccess: (progress) => {
      queryClient.setQueryData(queryKeys.setup.progress(businessId), progress);
    },
  });
}

// --- team -------------------------------------------------------------------

/** GET /business/businesses/:id/team */
export function useTeam(businessId: string | null): UseQueryResult<TeamMember[]> {
  return useQuery({
    queryKey: queryKeys.setup.team(businessId ?? "none"),
    queryFn: async () => {
      // The order number of this read, taken when it starts (see `useTeamFresh`).
      const started = nextTeamOrder();
      const items = (await api.get(`${base(businessId ?? "")}/team`, teamResponseSchema)).items;
      teamReadStarts.set(items, started);
      return items;
    },
    // Each read keeps its own array, so its order number stays with the data on the screen.
    structuralSharing: false,
    enabled: Boolean(businessId),
  });
}

/**
 * Team writes never put their answer in the Team list. The list comes only from server reads: each
 * write reads it again when it settles (a success, or a failure that may have saved first), and the
 * new read cancels a read in flight. Answers that arrive out of order, or a failure after a save,
 * cannot put an old invitation link or row id on the screen (internal review F7.2 to F11.2).
 * `useTeamFresh` tells a screen when the list it shows is safe to act on.
 */
const teamWriteKey = (businessId: string) => ["team-write", businessId] as const;

/**
 * One order for the page: each Team read takes a number when it starts, each team write takes one
 * when it settles. A read that started before a write settled (a read in flight that the refresh
 * reused, a read paused while offline) has a lower number than the write, so its data is not
 * trusted (internal review F12.1).
 */
let teamOrder = 0;
const nextTeamOrder = (): number => (teamOrder += 1);
const teamReadStarts = new WeakMap<TeamMember[], number>();
const teamWritesSettled = new Map<string, number>();

/** A team write settled: its number, then the setup data is read again. */
function teamWriteSettled(queryClient: QueryClient, businessId: string): void {
  teamWritesSettled.set(businessId, nextTeamOrder());
  refreshSetup(queryClient, businessId);
}

/**
 * True when the Team list on the screen came from a read that started after the last team write
 * settled, and no team write is in flight. The invitation panel offers a link or Revoke only then.
 */
export function useTeamFresh(businessId: string): boolean {
  const team = useTeam(businessId);
  const writing = useIsMutating({ mutationKey: teamWriteKey(businessId) }) > 0;
  const readStart = team.data ? (teamReadStarts.get(team.data) ?? 0) : 0;
  const fresh = !writing && readStart > (teamWritesSettled.get(businessId) ?? 0);
  // Old data and no read on the way (the refresh reused a read that started before the write): a
  // new read starts. A failed read waits for the owner's retry.
  const needsRead = !fresh && !writing && !team.isFetching && !team.isError && team.data !== undefined;
  const { refetch } = team;
  useEffect(() => {
    if (needsRead) void refetch();
  }, [needsRead, refetch]);
  return fresh;
}

export function useAddTeamMember(
  businessId: string,
): UseMutationResult<TeamMember, unknown, AddTeamMemberRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: teamWriteKey(businessId),
    mutationFn: (input) =>
      api.post(`${base(businessId)}/team`, addTeamMemberRequestSchema.parse(input), teamMemberSchema),
    // The first-run Team step counts the people it added itself (`onAdded`), so it does not need
    // the answer in the list.
    onSettled: () => teamWriteSettled(queryClient, businessId),
  });
}

export function useUpdateTeamRole(
  businessId: string,
): UseMutationResult<TeamMember[], unknown, { id: string; role: TeamRole }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: teamWriteKey(businessId),
    mutationFn: async (input) =>
      (await api.patch(`${base(businessId)}/team/${input.id}`, { role: input.role }, teamResponseSchema))
        .items,
    onSettled: () => teamWriteSettled(queryClient, businessId),
  });
}

/** POST /business/businesses/:id/team/:id/invitation: a new personal invitation. The old link stops working. */
export function useSendTeamInvitation(businessId: string): UseMutationResult<TeamMember[], unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: teamWriteKey(businessId),
    mutationFn: async (id) => (await api.post(`${base(businessId)}/team/${id}/invitation`, undefined, teamResponseSchema)).items,
    onSettled: () => teamWriteSettled(queryClient, businessId),
  });
}

/** DELETE /business/businesses/:id/team/:id/invitation: the person loses the team access until a new one. */
export function useRevokeTeamInvitation(businessId: string): UseMutationResult<TeamMember[], unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: teamWriteKey(businessId),
    mutationFn: async (id) => (await api.remove(`${base(businessId)}/team/${id}/invitation`, teamResponseSchema)).items,
    onSettled: () => teamWriteSettled(queryClient, businessId),
  });
}

export function useRemoveTeamMember(businessId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: teamWriteKey(businessId),
    mutationFn: (id) => api.delete(`${base(businessId)}/team/${id}`),
    onSettled: () => teamWriteSettled(queryClient, businessId),
  });
}

// --- import -----------------------------------------------------------------

/** GET /business/businesses/:id/imported-contacts */
export function useImportedContacts(businessId: string | null): UseQueryResult<ImportReview> {
  return useQuery({
    queryKey: queryKeys.setup.contacts(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/imported-contacts`, importReviewSchema),
    enabled: Boolean(businessId),
  });
}

export type ParsedContact = { displayName: string; phone: string };

/** POST /business/businesses/:id/imported-contacts */
export function useImportContacts(
  businessId: string,
): UseMutationResult<
  ImportReview,
  unknown,
  Omit<ImportContactsRequest, "purpose"> & { purpose?: "members" | "team" }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${base(businessId)}/imported-contacts`, { purpose: "members", ...input }, importReviewSchema),
    onSuccess: (review) => {
      queryClient.setQueryData(queryKeys.setup.contacts(businessId), review);
      refreshSetup(queryClient, businessId);
    },
  });
}

/** PATCH /business/businesses/:id/imported-contacts/:id, the Skip toggle. */
export function useUpdateImportedContact(
  businessId: string,
): UseMutationResult<
  ImportedContact,
  unknown,
  { id: string; classification?: ImportClassification; displayName?: string; phone?: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }) =>
      api.patch(`${base(businessId)}/imported-contacts/${id}`, patch, importedContactSchema),
    onSuccess: (contact) => {
      queryClient.setQueryData<ImportReview>(queryKeys.setup.contacts(businessId), (current) =>
        current
          ? { ...current, items: current.items.map((row) => (row.id === contact.id ? contact : row)) }
          : current,
      );
      refreshSetup(queryClient, businessId);
    },
  });
}

// --- timetable --------------------------------------------------------------

/** GET /business/businesses/:id/timetable */
export function useTimetable(businessId: string | null): UseQueryResult<Timetable> {
  return useQuery({
    queryKey: queryKeys.setup.timetable(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/timetable`, timetableSchema),
    enabled: Boolean(businessId),
  });
}

const templateResponseSchema = z.object({ cards: z.array(timetableCardSchema) });

/** GET /business/businesses/:id/timetable/template, the "set it up by hand" starter cards. */
export function useTimetableTemplate(businessId: string | null): UseQueryResult<TimetableCard[]> {
  return useQuery({
    queryKey: queryKeys.setup.template(businessId ?? "none"),
    queryFn: async () =>
      (await api.get(`${base(businessId ?? "")}/timetable/template`, templateResponseSchema)).cards,
    enabled: Boolean(businessId),
    staleTime: 10 * 60 * 1000,
  });
}

/** POST /business/businesses/:id/timetable/extract. The files are read together and not kept. */
export function useExtractTimetable(
  businessId: string,
): UseMutationResult<ExtractTimetableResponse, unknown, { files: TimetableFile[] }> {
  return useMutation({
    mutationFn: (input) =>
      api.post(`${base(businessId)}/timetable/extract`, input, extractTimetableResponseSchema, {
        timeoutMs: 90_000,
      }),
  });
}

/** PUT /business/businesses/:id/timetable. Generates sessions to the end of the term, or to the nightly horizon with no end date. A new timetable gets the default end. */
export function useSaveTimetable(businessId: string): UseMutationResult<Timetable, unknown, TimetableCard[]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cards) =>
      api.put(`${base(businessId)}/timetable`, { cards }, timetableSchema, { timeoutMs: 90_000 }),
    onSuccess: (timetable) => {
      queryClient.setQueryData(queryKeys.setup.timetable(businessId), timetable);
      refreshSetup(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.sessionsAll });
      void queryClient.invalidateQueries(businessScheduleQueries);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.groups(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.templates(businessId) });
      // New sessions and weekly bookings reach the member side too (an owner can be a member).
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessionsAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.scheduleAll });
      void queryClient.invalidateQueries({ queryKey: queryKeys.memberHomeAll });
    },
  });
}

// --- packages and rules -----------------------------------------------------

const packagesScreenSchema = packagesAndRulesSchema.extend({ fromTemplate: z.boolean() });
export type PackagesScreenData = z.infer<typeof packagesScreenSchema>;

/** GET /business/businesses/:id/packages. The sport template when nothing is saved yet. */
export function usePackagesAndRules(businessId: string | null): UseQueryResult<PackagesScreenData> {
  return useQuery({
    queryKey: queryKeys.setup.packages(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/packages`, packagesScreenSchema),
    enabled: Boolean(businessId),
  });
}

/** PUT /business/businesses/:id/packages */
export function useSavePackages(
  businessId: string,
): UseMutationResult<PackagesAndRules, unknown, SavePackagesRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.put(`${base(businessId)}/packages`, input, packagesAndRulesSchema),
    onSuccess: () => {
      refreshSetup(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.products(businessId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.packages(businessId) });
    },
  });
}

// --- invites ----------------------------------------------------------------

export function useInvitePreview(businessId: string | null): UseQueryResult<InvitePreview> {
  return useQuery({
    queryKey: queryKeys.setup.invitePreview(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/invites/preview`, invitePreviewSchema),
    enabled: Boolean(businessId),
  });
}

export function useInviteStatus(businessId: string | null): UseQueryResult<InviteStatus> {
  return useQuery({
    queryKey: queryKeys.setup.inviteStatus(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/invites/status`, inviteStatusSchema),
    enabled: Boolean(businessId),
  });
}

export type SendResult = z.infer<typeof sendResultSchema>;

export function useSendInvites(
  businessId: string,
): UseMutationResult<SendResult, unknown, { messageBody: string; dmEach: boolean; nudge: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`${base(businessId)}/invites/send`, input, sendResultSchema),
    onSuccess: () => refreshSetup(queryClient, businessId),
  });
}

export function useNudge(businessId: string): UseMutationResult<SendResult, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/invites/nudge`, undefined, sendResultSchema),
    onSuccess: () => refreshSetup(queryClient, businessId),
  });
}

// --- queues -----------------------------------------------------------------

const pendingMembersSchema = z.object({
  items: z.array(
    z.object({
      id: z.string().uuid(),
      userId: z.string().uuid(),
      name: z.string(),
      phoneE164: z.string().nullable(),
      phoneMasked: z.string(),
      requestedAt: z.string(),
    }),
  ),
});
export type PendingMember = z.infer<typeof pendingMembersSchema>["items"][number];

/** GET /business/businesses/:id/pending-members */
export function usePendingMembers(businessId: string | null): UseQueryResult<PendingMember[]> {
  return useQuery({
    queryKey: queryKeys.setup.pendingMembers(businessId ?? "none"),
    queryFn: async () =>
      (await api.get(`${base(businessId ?? "")}/pending-members`, pendingMembersSchema)).items,
    enabled: Boolean(businessId),
  });
}

export function useDecidePendingMember(
  businessId: string,
): UseMutationResult<{ ok: true }, unknown, { id: string; decision: "approve" | "decline" }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      api.post(`${base(businessId)}/pending-members/${input.id}/${input.decision}`, undefined, okSchema),
    onSuccess: () => {
      refreshSetup(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.members(businessId) });
    },
  });
}

/** GET /business/businesses/:id/participants */
export function useParticipants(businessId: string | null): UseQueryResult<OwnerParticipant[]> {
  return useQuery({
    queryKey: queryKeys.setup.participants(businessId ?? "none"),
    queryFn: async () =>
      (await api.get(`${base(businessId ?? "")}/participants`, participantsResponseSchema)).items,
    enabled: Boolean(businessId),
  });
}

/**
 * The options of `usePlaceParticipant`. A placement can be saved on the server and its answer can
 * still be lost: a failure reads the setup lists again too, so the levels board shows where the
 * server has the child (internal review of the board drag, F2.1).
 */
export function placeParticipantOptions(queryClient: QueryClient, businessId: string) {
  return {
    mutationFn: ({ id, ...body }: PlaceParticipantRequest & { id: string }) =>
      api.patch(`${base(businessId)}/participants/${id}`, body, ownerParticipantSchema),
    onSuccess: (participant: OwnerParticipant) => {
      queryClient.setQueryData<OwnerParticipant[]>(queryKeys.setup.participants(businessId), (current) =>
        current?.map((row) => (row.id === participant.id ? participant : row)),
      );
      refreshSetup(queryClient, businessId);
    },
    onError: () => refreshSetup(queryClient, businessId),
  };
}

/** PATCH /business/businesses/:id/participants/:id: place, mark unsure, or confirm. */
export function usePlaceParticipant(
  businessId: string,
): UseMutationResult<OwnerParticipant, unknown, PlaceParticipantRequest & { id: string }> {
  const queryClient = useQueryClient();
  return useMutation(placeParticipantOptions(queryClient, businessId));
}

// --- starting balances ------------------------------------------------------

export function useStartingBalances(businessId: string | null): UseQueryResult<StartingBalances> {
  return useQuery({
    queryKey: queryKeys.setup.balances(businessId ?? "none"),
    queryFn: () => api.get(`${base(businessId ?? "")}/starting-balances`, startingBalancesSchema),
    enabled: Boolean(businessId),
  });
}

export function useSaveStartingBalances(
  businessId: string,
): UseMutationResult<StartingBalances, unknown, SaveStartingBalancesRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`${base(businessId)}/starting-balances`, input, startingBalancesSchema),
    onSuccess: (balances) => {
      queryClient.setQueryData(queryKeys.setup.balances(businessId), balances);
      refreshSetup(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.credits });
      void queryClient.invalidateQueries({ queryKey: queryKeys.owner.memberCreditsAll });
    },
  });
}

// --- schedule ---------------------------------------------------------------

/** GET /business/businesses/:id/schedule?from&to, with staff, fill and roster. */
export function useBusinessSchedule(
  businessId: string | null,
  from: string,
  to: string,
): UseQueryResult<BusinessSession[]> {
  return useQuery({
    queryKey: queryKeys.setup.schedule(businessId ?? "none", from, to),
    queryFn: async () =>
      (
        await api.get(`${base(businessId ?? "")}/schedule`, ownerScheduleResponseSchema, {
          query: { from, to },
        })
      ).items,
    enabled: Boolean(businessId),
    staleTime: 30 * 1000,
  });
}
